import { playsContainingAll } from '../../functions';
import {
  ISourcedCard,
  suitToNamed,
  valueToNamed,
} from '../../Models/CardModels';
import { IPlay } from '../../Models/StateModels';

interface ICardSelectionOptions {
  cards: ISourcedCard[];
  availablePlays: IPlay[];
  isTurn: boolean;
  selectedCardIds: string[];
  rejectedCardIds: string[];
  onSelectionChange: (cardIds: string[]) => void;
  onReject: (cardIds: string[]) => void;
}

function cardName({ card }: ISourcedCard) {
  return 'value' in card
    ? `${valueToNamed(card.value)} of ${suitToNamed(card.suit)}`
    : 'Face-down card';
}

export function cardAriaLabel(
  sourcedCard: ISourcedCard,
  isSelected: boolean,
  isPlayable: boolean
) {
  if (isSelected) {
    return `${cardName(sourcedCard)}, selected`;
  }
  return isPlayable
    ? cardName(sourcedCard)
    : `${cardName(sourcedCard)}, not playable`;
}

export type CardSelection = ReturnType<typeof getCardSelection>;

export function getCardSelection({
  cards,
  availablePlays,
  isTurn,
  selectedCardIds,
  rejectedCardIds,
  onSelectionChange,
  onReject,
}: ICardSelectionOptions) {
  const cardIds = cards.map((c) => c.card.cardId);
  const plays = isTurn
    ? availablePlays.filter((play) =>
        play.cardsToPlay.every((c) => cardIds.includes(c.card.cardId))
      )
    : [];
  const selectedIds = selectedCardIds.filter((id) => cardIds.includes(id));
  const selectedCards = cards.filter((c) =>
    selectedIds.includes(c.card.cardId)
  );
  const hasSelection = selectedIds.length > 0;

  const isPlayableWith = (ids: string[]) =>
    playsContainingAll(plays, ids).length > 0;

  const valueOf = (cardId: string) => {
    const sourcedCard = cards.find((c) => c.card.cardId === cardId);
    return sourcedCard && 'value' in sourcedCard.card
      ? sourcedCard.card.value
      : undefined;
  };

  const isSameValue = (ids: string[]) =>
    ids.length <= 1 ||
    (valueOf(ids[0]) !== undefined &&
      ids.every((id) => valueOf(id) === valueOf(ids[0])));

  const isValidSelection = (ids: string[]) =>
    isTurn ? isPlayableWith(ids) : isSameValue(ids);

  const canToggle = (cardId: string) =>
    cardIds.includes(cardId) &&
    (selectedIds.includes(cardId) ||
      isValidSelection([...selectedIds, cardId]));

  const isRejected = (cardId: string) => rejectedCardIds.includes(cardId);

  const toggleSelected = (cardId: string) =>
    onSelectionChange(
      selectedIds.includes(cardId)
        ? selectedIds.filter((id) => id !== cardId)
        : [...selectedIds, cardId]
    );

  const tapCard = (cardId: string) =>
    canToggle(cardId) ? toggleSelected(cardId) : onReject([cardId]);

  const isAllSelected = (groupIds: string[]) =>
    groupIds.every((id) => selectedIds.includes(id));

  const withGroup = (groupIds: string[]) => [
    ...selectedIds,
    ...groupIds.filter((id) => !selectedIds.includes(id)),
  ];

  const canToggleGroup = (groupIds: string[]) =>
    groupIds.every((id) => cardIds.includes(id)) &&
    (isAllSelected(groupIds) || isValidSelection(withGroup(groupIds)));

  const toggleGroup = (groupIds: string[]) =>
    onSelectionChange(
      isAllSelected(groupIds)
        ? selectedIds.filter((id) => !groupIds.includes(id))
        : withGroup(groupIds)
    );

  return {
    selectedIds,
    selectedCards,
    hasSelection,
    isPlayableWith,
    canToggle,
    isRejected,
    tapCard,
    canToggleGroup,
    toggleGroup,
  };
}
